import React, { useEffect, useState, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { User, Tenant, PaginatedResponse } from '../types';
import { fetchUsers, createUser, updateUser, deleteUser, updateUserRole, changeUserPassword, fetchTenantsList } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSuperTenant } from '../context/SuperAdminTenantContext';

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8001/api';

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super Admin', tenant_admin: 'Admin', manager: 'Manager',
  marketing: 'Marketing', agent: 'Agent', external_user: 'External User',
};

const ROLES = [
  { value: '', label: 'All Roles' },
  { value: 'tenant_admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'agent', label: 'Agent' },
  { value: 'external_user', label: 'External User (read-only)' },
];

const AdminUsers: React.FC = () => {
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === 'super_admin';
  const { tenantId: globalTenantId } = useSuperTenant();

  const [data, setData]     = useState<PaginatedResponse<User> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  // ── Filters ──
  const [search, setSearch]           = useState('');
  const [roleFilter, setRoleFilter]   = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [page, setPage]               = useState(1);
  const [tenants, setTenants]         = useState<Tenant[]>([]);

  // ── Create modal ──
  const [showCreate, setShowCreate]   = useState(false);
  const [cName, setCName]     = useState('');
  const [cEmail, setCEmail]   = useState('');
  const [cMobile, setCMobile] = useState('');
  const [cPwd, setCPwd]       = useState('');
  const [cRole, setCRole]     = useState<'tenant_admin' | 'manager' | 'marketing' | 'agent' | 'external_user'>('agent');
  const [cTenant, setCTenant] = useState('');
  const [cSaving, setCSaving] = useState(false);
  const [cError, setCError]   = useState('');

  // ── Edit modal ──
  const [editUser, setEditUser]   = useState<User | null>(null);
  const [eName, setEName]         = useState('');
  const [eEmail, setEEmail]       = useState('');
  const [eMobile, setEMobile]     = useState('');
  const [eRole, setERole]         = useState<'tenant_admin' | 'manager' | 'marketing' | 'agent' | 'external_user'>('agent');
  const [eSaving, setESaving]     = useState(false);
  const [eError, setEError]       = useState('');

  // ── Delete confirm ──
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleting, setDeleting]         = useState(false);
  const [delError, setDelError]         = useState('');

  // ── QR modal ──
  const [qrUser, setQrUser] = useState<User | null>(null);

  // ── Password modal ──
  const [pwdUser, setPwdUser]     = useState<User | null>(null);
  const [newPwd, setNewPwd]       = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError]   = useState('');
  const [pwdDone, setPwdDone]     = useState(false);

  // Load tenants list for super_admin dropdown
  useEffect(() => {
    if (isSuperAdmin) {
      fetchTenantsList().then(setTenants).catch(() => {});
    }
  }, [isSuperAdmin]);

  // Keep local tenant filter in sync with the global super admin bar selection
  useEffect(() => {
    setTenantFilter(globalTenantId ? String(globalTenantId) : '');
  }, [globalTenantId]);

  const load = useCallback(() => {
    setLoading(true);
    const params: Record<string, any> = { page };
    if (search)       params.search    = search;
    if (roleFilter)   params.role      = roleFilter;
    if (isSuperAdmin && tenantFilter) params.tenant_id = tenantFilter;
    fetchUsers(params)
      .then(setData)
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  }, [page, search, roleFilter, tenantFilter, isSuperAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, roleFilter, tenantFilter]);

  // ── Create ──────────────────────────────────────────────────────────────

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCSaving(true); setCError('');
    try {
      const payload: any = { name: cName, email: cEmail, mobile: cMobile || undefined, password: cPwd, role: cRole };
      if (isSuperAdmin && cTenant) payload.tenant_id = parseInt(cTenant);
      const u = await createUser(payload);
      load();
      setShowCreate(false);
      setCName(''); setCEmail(''); setCMobile(''); setCPwd(''); setCRole('agent'); setCTenant('');
    } catch (err: any) {
      const d = err?.response?.data;
      if (d?.errors) setCError(Object.values(d.errors as Record<string, string[]>).flat().join(' '));
      else setCError(d?.message ?? 'Failed to create user');
    } finally { setCSaving(false); }
  };

  // ── Edit ─────────────────────────────────────────────────────────────────

  const openEdit = (u: User) => {
    setEditUser(u);
    setEName(u.name); setEEmail(u.email);
    setEMobile(u.mobile ?? '');
    setERole((u.role === 'super_admin' ? 'tenant_admin' : u.role) as 'tenant_admin' | 'manager' | 'marketing' | 'agent' | 'external_user');
    setEError('');
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    setESaving(true); setEError('');
    try {
      const updated = await updateUser(editUser!.id, {
        name: eName, email: eEmail, mobile: eMobile || undefined, role: eRole,
      });
      setData(prev => prev ? { ...prev, data: prev.data.map(u => u.id === updated.id ? { ...u, ...updated } : u) } : prev);
      setEditUser(null);
    } catch (err: any) {
      const d = err?.response?.data;
      if (d?.errors) setEError(Object.values(d.errors as Record<string, string[]>).flat().join(' '));
      else setEError(d?.message ?? 'Failed to update user');
    } finally { setESaving(false); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    setDeleting(true); setDelError('');
    try {
      await deleteUser(deleteTarget!.id);
      load();
      setDeleteTarget(null);
    } catch (err: any) {
      setDelError(err?.response?.data?.message ?? 'Failed to delete user');
    } finally { setDeleting(false); }
  };

  // ── Role inline change ────────────────────────────────────────────────────

  const handleRoleChange = async (u: User, newRole: 'tenant_admin' | 'manager' | 'marketing' | 'agent' | 'external_user') => {
    try {
      const updated = await updateUserRole(u.id, newRole);
      setData(prev => prev ? { ...prev, data: prev.data.map(x => x.id === updated.id ? { ...x, ...updated } : x) } : prev);
    } catch { alert('Failed to update role'); }
  };

  // ── Password ──────────────────────────────────────────────────────────────

  const openPwd = (u: User) => { setPwdUser(u); setNewPwd(''); setConfirmPwd(''); setPwdError(''); setPwdDone(false); };

  const handleChangePwd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) { setPwdError('Passwords do not match'); return; }
    if (newPwd.length < 6)    { setPwdError('Minimum 6 characters'); return; }
    setPwdSaving(true); setPwdError('');
    try {
      await changeUserPassword(pwdUser!.id, newPwd);
      setPwdDone(true);
      setTimeout(() => setPwdUser(null), 1500);
    } catch (err: any) {
      setPwdError(err?.response?.data?.message ?? 'Failed to update password');
    } finally { setPwdSaving(false); }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const canActOn = (u: User) =>
    u.role !== 'super_admin' && (isSuperAdmin || u.tenant_id === me?.tenant_id);

  const users = data?.data ?? [];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="admin-page">
      <div className="page-hd">
        <div className="page-hd-left">
          <h1 className="page-title">User Management</h1>
          <p className="page-meta">
            {data ? <><strong>{data.total}</strong> users</> : 'Create and manage users across roles.'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ Add User</button>
      </div>

      {/* ── Filter bar ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="filter-input-wrap" style={{ flex: '1 1 200px', minWidth: 160 }}>
          <svg className="filter-input-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="filter-input"
            placeholder="Search name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {isSuperAdmin && (
          <select
            className="filter-input"
            style={{ minWidth: 160, paddingLeft: 10 }}
            value={tenantFilter}
            onChange={e => setTenantFilter(e.target.value)}
          >
            <option value="">All Tenants</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        )}

        <select
          className="filter-input"
          style={{ minWidth: 140, paddingLeft: 10 }}
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
        >
          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>

        {(search || roleFilter || tenantFilter) && (
          <button
            className="btn-secondary"
            style={{ whiteSpace: 'nowrap', fontSize: 13 }}
            onClick={() => { setSearch(''); setRoleFilter(''); setTenantFilter(''); }}
          >
            Clear
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="data-card">
        {loading ? (
          <div className="spinner">Loading…</div>
        ) : (
          <>
            <div className="data-card-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 54 }}>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Role</th>
                    <th>Joined</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr className="table-empty-row">
                      <td colSpan={7}>No users found.</td>
                    </tr>
                  ) : users.map(u => (
                    <tr key={u.id}>
                      <td>
                        <span style={{ display: 'inline-block', background: '#f1f5f9', color: '#475569', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700, fontFamily: 'monospace' }}>
                          #{u.id}
                        </span>
                      </td>
                      <td>
                        <div className="avatar-chip">
                          <div className="avatar-sm">{u.name.charAt(0).toUpperCase()}</div>
                          <div>
                            <span className="avatar-sm-name">{u.name}</span>
                            {isSuperAdmin && u.tenant && (
                              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{u.tenant.name}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="td-muted">{u.email}</td>
                      <td className="td-muted" style={{ fontFamily: 'monospace', fontSize: 13 }}>{u.mobile ?? '—'}</td>
                      <td>
                        <span className={`badge ${u.role === 'tenant_admin' ? 'badge-qualified' : 'badge-new'}`} style={{ marginRight: 8 }}>
                          {ROLE_LABEL[u.role] ?? u.role}
                        </span>
                        {canActOn(u) && (
                          <select
                            style={{ height: 28, padding: '0 8px', fontSize: 11, width: 'auto', display: 'inline-block' }}
                            value={u.role}
                            onChange={e => handleRoleChange(u, e.target.value as 'tenant_admin' | 'manager' | 'marketing' | 'agent' | 'external_user')}
                          >
                            <option value="agent">Agent</option>
                            <option value="manager">Manager</option>
                            <option value="marketing">Marketing</option>
                            <option value="tenant_admin">Admin</option>
                            <option value="external_user">External User (read-only)</option>
                          </select>
                        )}
                      </td>
                      <td className="td-muted">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                          {canActOn(u) && (
                            <button onClick={() => openEdit(u)} title="Edit user" style={btnStyle('#dbeafe', '#1d4ed8')}>
                              ✏️ Edit
                            </button>
                          )}
                          {canActOn(u) && (
                            <button onClick={() => openPwd(u)} title="Reset password" style={btnStyle('#fef3c7', '#92400e')}>
                              🔑 Pwd
                            </button>
                          )}
                          {u.role === 'agent' && (
                            <button onClick={() => setQrUser(u)} title="Device QR code" style={btnStyle('#ede9fe', '#7c3aed')}>
                              📱 QR
                            </button>
                          )}
                          {canActOn(u) && u.id !== me?.id && (
                            <button onClick={() => { setDeleteTarget(u); setDelError(''); }} title="Delete user" style={btnStyle('#fee2e2', '#dc2626')}>
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {data && data.last_page > 1 && (
              <div className="pager">
                <span>Page {data.current_page} of {data.last_page} &middot; {data.total} total</span>
                <div className="pager-btns">
                  <button className="pager-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  {Array.from({ length: data.last_page }, (_, i) => i + 1).map(p => (
                    <button key={p} className={`pager-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                  <button className="pager-btn" disabled={page === data.last_page} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Edit User Modal ─────────────────────────────────────────────── */}
      {editUser && (
        <Modal title={`Edit User — ${editUser.name}`} onClose={() => setEditUser(null)}>
          <form onSubmit={handleEdit}>
            <FormField label="Full Name" value={eName} onChange={setEName} required />
            <FormField label="Email" type="email" value={eEmail} onChange={setEEmail} required />
            <FormField label="Mobile" value={eMobile} onChange={setEMobile} placeholder="(optional)" />
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Role</label>
              <select className="form-input" value={eRole} onChange={e => setERole(e.target.value as any)} style={{ width: '100%' }}>
                <option value="agent">Agent</option>
                <option value="manager">Manager</option>
                <option value="marketing">Marketing</option>
                <option value="tenant_admin">Admin</option>
                <option value="external_user">External User (read-only)</option>
              </select>
            </div>
            {eError && <ErrorBox msg={eError} />}
            <ModalFooter onCancel={() => setEditUser(null)} saving={eSaving} label="Save Changes" />
          </form>
        </Modal>
      )}

      {/* ── Delete Confirm Modal ────────────────────────────────────────── */}
      {deleteTarget && (
        <Modal title="Delete User" onClose={() => setDeleteTarget(null)}>
          <p style={{ fontSize: 14, color: 'var(--text)', marginBottom: 8 }}>
            Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
          </p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
            This action cannot be undone. If this user has assigned leads or deals, you must reassign them first.
          </p>
          {delError && <ErrorBox msg={delError} />}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 600, fontSize: 14, cursor: deleting ? 'default' : 'pointer', opacity: deleting ? 0.7 : 1 }}
            >
              {deleting ? 'Deleting…' : 'Yes, Delete'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── QR Modal ────────────────────────────────────────────────────── */}
      {qrUser && (
        <Modal title="Register Device" onClose={() => setQrUser(null)}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, textAlign: 'center' }}>
            Agent: <strong>{qrUser.name}</strong>
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <div style={{ padding: 16, background: '#fff', border: '1px solid var(--border)', borderRadius: 10 }}>
              <QRCodeSVG value={JSON.stringify({ tenant_id: qrUser.tenant_id, user_id: qrUser.id, api_url: API_URL })} size={200} level="M" />
            </div>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.6, marginBottom: 16 }}>
            Open the Automately Android app → tap <strong>Scan QR</strong> to register this device.
          </p>
          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', fontSize: 11, fontFamily: 'monospace', color: '#475569', marginBottom: 20 }}>
            <div>tenant_id: <strong>{qrUser.tenant_id}</strong></div>
            <div>user_id: <strong>{qrUser.id}</strong></div>
            <div>api_url: <strong>{API_URL}</strong></div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <button className="btn-secondary" onClick={() => setQrUser(null)}>Close</button>
          </div>
        </Modal>
      )}

      {/* ── Reset Password Modal ─────────────────────────────────────────── */}
      {pwdUser && (
        <Modal title="Reset Password" onClose={() => setPwdUser(null)}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
            Setting new password for <strong>{pwdUser.name}</strong>
          </p>
          {pwdDone ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
              <div style={{ fontWeight: 600, color: '#166534' }}>Password updated successfully</div>
            </div>
          ) : (
            <form onSubmit={handleChangePwd}>
              <FormField label="New Password" type="password" value={newPwd} onChange={setNewPwd} required placeholder="Min 6 characters" />
              <FormField label="Confirm Password" type="password" value={confirmPwd} onChange={setConfirmPwd} required placeholder="Re-enter password" />
              {pwdError && <ErrorBox msg={pwdError} />}
              <ModalFooter onCancel={() => setPwdUser(null)} saving={pwdSaving} label="Update Password" />
            </form>
          )}
        </Modal>
      )}

      {/* ── Create User Modal ────────────────────────────────────────────── */}
      {showCreate && (
        <Modal title="Add User" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleCreate}>
            <FormField label="Full Name" value={cName} onChange={setCName} required placeholder="e.g. Rahul Sharma" />
            <FormField label="Email" type="email" value={cEmail} onChange={setCEmail} required placeholder="rahul@company.com" />
            <FormField label="Mobile" value={cMobile} onChange={setCMobile} placeholder="(optional)" />
            <FormField label="Password" type="password" value={cPwd} onChange={setCPwd} required placeholder="Min 6 characters" />
            {isSuperAdmin && (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Tenant <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span></label>
                <select className="form-input" value={cTenant} onChange={e => setCTenant(e.target.value)} required style={{ width: '100%' }}>
                  <option value="">Select tenant…</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Role</label>
              <select className="form-input" value={cRole} onChange={e => setCRole(e.target.value as any)} style={{ width: '100%' }}>
                <option value="agent">Agent</option>
                <option value="manager">Manager</option>
                <option value="marketing">Marketing</option>
                <option value="tenant_admin">Admin</option>
                <option value="external_user">External User (read-only)</option>
              </select>
            </div>
            {cError && <ErrorBox msg={cError} />}
            <ModalFooter onCancel={() => setShowCreate(false)} saving={cSaving} label="Create User" />
          </form>
        </Modal>
      )}
    </div>
  );
};

// ── Shared small components ──────────────────────────────────────────────────

const btnStyle = (bg: string, color: string): React.CSSProperties => ({
  background: bg, color, border: 'none', borderRadius: 6,
  padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
});

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6,
};

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div style={{ background: '#fff', borderRadius: 12, padding: '28px 32px', width: 420, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>{title}</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8', lineHeight: 1 }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

const FormField: React.FC<{ label: string; value: string; onChange: (v: string) => void; required?: boolean; placeholder?: string; type?: string }> = ({ label, value, onChange, required, placeholder, type = 'text' }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={labelStyle}>{label}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}</label>
    <input type={type} className="form-input" required={required} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} style={{ width: '100%' }} />
  </div>
);

const ErrorBox: React.FC<{ msg: string }> = ({ msg }) => (
  <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 7, padding: '8px 12px', fontSize: 13, marginBottom: 14 }}>{msg}</div>
);

const ModalFooter: React.FC<{ onCancel: () => void; saving: boolean; label: string }> = ({ onCancel, saving, label }) => (
  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
    <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
    <button type="submit" className="btn-primary" disabled={saving} style={{ minWidth: 130 }}>{saving ? 'Saving…' : label}</button>
  </div>
);

export default AdminUsers;
